import type { ButtonV2Props } from '@withjoy/joykit';
import type { TravelDistancesBetweenPointsResponseFragment } from '@graphql/generated';

export type HotelTilePhotoData = Readonly<{ src: string; alt: string }>;

export type HotelTileDistanceData = Readonly<{
  milesToVenue?: number | null;
  distances?: Maybe<TravelDistancesBetweenPointsResponseFragment>;
}>;

export type HotelTilePricingData = Readonly<{
  price: string;
  strikeoutPrice?: string;
  percentageOff?: number;
  eventOnlyRate?: boolean;
  soldOut?: boolean;
  soldOutUrl?: string;
}>;

export enum AccommodationCombinedType {
  Roomblock = 'Roomblock',
  Hotel = 'Hotel',
  Custom = 'Custom',
}

export type HotelTileRoomBlockPricing = Readonly<{
  pricePerNight: number;
  strikeoutPricePerNight?: number;
  cutoffDate?: string;
}>;

export type HotelTilePricingProps = Readonly<{
  // Mode 1: Pass pre-computed pricing data
  data?: HotelTilePricingData;
  // Mode 2: Pass accommodation ID to fetch internally
  accommodationId?: string;
  // Mode 3: Room block pricing
  roomBlock?: HotelTileRoomBlockPricing;
}>;

export type HotelTileCTAProps = Readonly<{
  title: string;
  url?: string;
  variant?: ButtonV2Props['variant'];
  isEnabled?: boolean;
  onClick?: (e: React.MouseEvent) => void;
}>;

export type HotelTileTagsData = Readonly<{
  reservationCount?: number;
  interestedGuestCount?: number;
  showDiscountBadge?: boolean;
  discountPercentage?: number;
  accommodationType?: string;
}>;

export type HotelTileLayoutConfig = Readonly<{
  enableHorizontalView?: boolean;
  enableHoverShadow?: boolean;
  showIndex?: number;
  hotelLocation?: Readonly<{ lat: number; lng: number }>;
  venueLocation?: Readonly<{ lat: number; lng: number }> | null;
}>;

export type HotelTileEventContext = Readonly<{
  eventDisplayName?: string;
  eventPhoto?: string | null;
}>;

export interface HotelTileCardProps
  extends Readonly<{
    // MANDATORY
    id: string;
    displayName: string;

    // OPTIONAL - Display Data
    description?: string;
    starRating?: number;
    photos?: ReadonlyArray<HotelTilePhotoData>;
    distance?: HotelTileDistanceData;

    // OPTIONAL - Pricing
    pricing?: HotelTilePricingProps;

    // OPTIONAL - CTA Configuration
    cta?: HotelTileCTAProps;

    // OPTIONAL - Tags/Badges
    tags?: HotelTileTagsData;

    // OPTIONAL - Layout & Behavior
    layout?: HotelTileLayoutConfig;

    // OPTIONAL - Event context (for tooltips)
    eventContext?: HotelTileEventContext;

    // OPTIONAL - Interaction handlers
    onMouseEnter?: () => void;
    onMouseLeave?: () => void;
    onClick?: (e: React.MouseEvent) => void;

    // OPTIONAL - Link behavior
    linkProps?: Readonly<{
      href?: string;
      target?: string;
    }>;
  }> {}
